import { TreeRenderProps } from 'react-complex-tree';
import { Classes, Collapse, Colors, Icon } from '@blueprintjs/core';
import { StyledRequiredLabel } from 'builder/components/WidgetConfigurations/DragDropBeautifulDnD';
import {
  cx,
  renderItemIcon,
  handleContextMenuClick,
} from './navRenderers.util';
import { Icon as ToolkitIcon } from 'shared/theme/link-ui-react/Icon';

/**
 * Helper function to calculate the styles for the tree item container.
 *
 * @param {boolean} isFolder - Whether the item is a folder.
 * @param {number} depth - The depth of the tree item in the hierarchy.
 * @returns {React.CSSProperties} - The calculated styles for the container.
 */
const getContainerStyle = (
  isFolder: boolean,
  depth: number
): React.CSSProperties => {
  if (!isFolder && depth === 0) {
    return { marginLeft: '16px' };
  }
  return isFolder
    ? { paddingLeft: `${depth * 16}px` }
    : { paddingLeft: '25px' };
};

/**
 * Helper function to calculate the styles for the collapse container.
 *
 * @param {boolean} isExpanded - Whether the folder is expanded.
 * @param {boolean} hasArrow - Whether the item has an arrow.
 * @returns {React.CSSProperties} - The calculated styles for the collapse container.
 */
const getCollapseStyle = (
  isExpanded: boolean,
  hasArrow: boolean
): React.CSSProperties => {
  if (isExpanded) {
    return {
      height: 'auto',
      overflowY: 'visible',
      transition: 'none 0s ease 0s',
      marginLeft: hasArrow ? '20px' : '45px',
    };
  }
  return {};
};

/**
 * Renders an invisible 28px placeholder list item for an empty section folder.
 * The element is physically present so react-complex-tree can detect drops
 * against it, but hidden from users via opacity: 0 and pointer-events: none.
 */
const renderPlaceholderItem = (context: any): JSX.Element => (
  <li
    {...context.itemContainerWithChildrenProps}
    {...context.itemContainerWithoutChildrenProps}
    {...context.interactiveElementProps}
    style={{
      height: '28px',
      listStyle: 'none',
      opacity: 0,
      pointerEvents: 'none',
    }}
  />
);

/**
 * Custom renderers for the tree structure used in the site builder.
 */
export const navRenderers: TreeRenderProps = {
  /**
   * Renders the container for the tree.
   *
   * @param {object} props - The properties for the tree container.
   * @returns {JSX.Element} - The rendered tree container.
   */
  renderTreeContainer: props => (
    <div className={cx(Classes.TREE)}>
      <ul
        className={cx(Classes.TREE_ROOT, Classes.TREE_NODE_LIST)}
        {...props.containerProps}
      >
        {props.children}
      </ul>
    </div>
  ),

  /**
   * Renders the container for tree items.
   *
   * @param {object} props - The properties for the items container.
   * @returns {JSX.Element} - The rendered items container.
   */
  renderItemsContainer: props => (
    <ul className={cx(Classes.TREE_NODE_LIST)} {...props.containerProps}>
      {props.children}
    </ul>
  ),

  /**
   * Renders an individual tree item.
   *
   * @param {object} props - The properties for the tree item.
   * @returns {JSX.Element} - The rendered tree item.
   */
  renderItem: props => {
    const { item, context, depth, arrow, title, children } = props;

    // Synthetic placeholder items are injected into empty section folders so
    // react-complex-tree has a real DOM element to target during drag-and-drop.
    // Render them as invisible but physically present (28px height) so the
    // drop-detection hit area covers the whole empty folder area.
    if (item.data?.isPlaceholder) {
      return renderPlaceholderItem(context);
    }

    // Calculate styles for the container and collapse sections
    const containerStyle = getContainerStyle(item.isFolder, depth);
    const collapseStyle = getCollapseStyle(context.isExpanded, !!arrow);

    return (
      <li
        className={cx(
          Classes.TREE_NODE,
          (context.isSelected || context.isDraggingOver) &&
            Classes.TREE_NODE_SELECTED
        )}
        {...(context.itemContainerWithChildrenProps as any)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          fontFamily: 'OptumSans',
        }}
      >
        {/* Tree Item Content */}
        <div
          className={cx(
            Classes.TREE_NODE_CONTENT,
            `${Classes.TREE_NODE_CONTENT}-${depth}`
          )}
          {...(context.itemContainerWithoutChildrenProps as any)}
          {...(context.interactiveElementProps as any)}
          style={containerStyle}
        >
          {/* Render the arrow for folders or a placeholder for non-folders */}
          {item.isFolder ? (
            arrow
          ) : (
            <span className={Classes.TREE_NODE_CARET_NONE} />
          )}

          {/* Render the item icon if available */}
          {item.data.icon !== undefined
            ? item.data.icon === null
              ? null
              : renderItemIcon(item, context)
            : renderItemIcon(item, context)}

          {/* Render the item title */}
          {'  '}
          {title}

          {/* Context menu icon */}
          <ToolkitIcon
            icon={'MoreVertical'}
            id="nav-context-menu"
            data-test-id="nav-context-menu"
            style={{ position: 'relative' }}
            width={15}
            height={15}
            onClick={e => handleContextMenuClick(e, item, item.isFolder)}
          />

          {/* Required label for specific items */}
          {item.data.required && (
            <StyledRequiredLabel>*Required</StyledRequiredLabel>
          )}
        </div>

        {/* Render children if the folder is expanded */}
        <div className={cx(Classes.COLLAPSE)} style={collapseStyle}>
          <Collapse isOpen={context.isExpanded} transitionDuration={0}>
            {children}
          </Collapse>
        </div>
      </li>
    );
  },

  /**
   * Renders the arrow for expanding or collapsing a tree item.
   *
   * @param {object} props - The properties for the arrow.
   * @returns {JSX.Element} - The rendered arrow.
   */
  renderItemArrow: props => (
    <Icon
      icon={props.context.isExpanded ? 'chevron-down' : 'chevron-right'}
      className={cx(
        Classes.TREE_NODE_CARET,
        props.context.isExpanded
          ? Classes.TREE_NODE_CARET_OPEN
          : Classes.TREE_NODE_CARET_CLOSED
      )}
      {...(props.context.arrowProps as any)}
    />
  ),

  /**
   * Renders the line indicating where an item can be dragged between other items.
   *
   * @param {object} props - The properties for the drag line.
   * @returns {JSX.Element} - The rendered drag line.
   */
  renderDragBetweenLine: ({ draggingPosition, lineProps }) => (
    <div
      {...lineProps}
      style={{
        position: 'absolute',
        right: '0',
        top:
          draggingPosition.targetType === 'between-items' &&
          draggingPosition.linePosition === 'top'
            ? '0px'
            : draggingPosition.targetType === 'between-items' &&
              draggingPosition.linePosition === 'bottom'
            ? '-4px'
            : '-2px',
        left: `${draggingPosition.depth * 16}px`,
        height: '4px',
        backgroundColor: Colors.BLUE3,
      }}
    />
  ),

  /**
   * Specifies the offset for rendering tree item depths.
   * Set to 16 to match the drag indicator line position (depth * 16px),
   * so the reparent-upwards zone aligns with the visual drop indicator.
   */
  renderDepthOffset: 16,
};
